/**
 * HtmlResponseError — typed rejection for "the JSON endpoint answered with HTML"
 * (AAS-I18N-002 W8 / user ruling 23, 2026-08-10).
 *
 * ## The defect this exists to make impossible to ship again
 *
 * patrol-web's `apiClient` runs with `baseURL: ''`, so every caller must pass a
 * full `/api/v1/patrol/...` path. Five list-view/dashboard preference call sites
 * used the shared page-shell defaults (`/user-preferences/list-views`,
 * `/user-preferences/dashboard`, `/dashboard/group-configs`), which assume the
 * client's baseURL already carries the prefix. Composed against an empty
 * baseURL those resolve to the **web origin**, where Vite's SPA history
 * fallback (dev) and the static SPA host (prod) answer `index.html` with a
 * **200 OK**.
 *
 * A 200 means axios resolves. The client then read `response.data` — a string
 * of HTML — found no `data` key on it, and returned an empty settings map. No
 * throw, no console error, no failing test. The user changed a column layout,
 * saw no error, and the change was gone. **Silent data loss.**
 *
 * The 200 is precisely what hid it: every other failure mode in this codebase
 * announces itself with a non-2xx. So the shape "successful response whose body
 * is HTML on a client that asked for JSON" is elevated here into a loud,
 * typed failure.
 *
 * ## Why this is not merely a patrol-web guard
 *
 * Any app whose `baseURL` does not already carry the API prefix can compose the
 * same URL, and any deploy that mis-routes `/api` behind a CDN produces the same
 * 200-with-HTML for EVERY endpoint. Catching it at the shared client means the
 * next occurrence surfaces as an error naming the URL, instead of as a
 * user-visible "my settings don't save".
 *
 * @module @puffin-core/api-client/errors/HtmlResponseError
 */

/**
 * Typed rejection raised when a JSON request receives an HTML response body.
 *
 * Properties:
 * - `name`        — always `'HtmlResponseError'`
 * - `code`        — always `'HTML_RESPONSE'`
 * - `status`      — the HTTP status that carried the HTML (usually 200)
 * - `endpoint`    — the request URL as the caller wrote it (pre-baseURL)
 * - `requestUrl`  — the fully composed URL when axios exposes one, else `endpoint`
 * - `method`      — HTTP method, lowercased
 * - `contentType` — the response `content-type`, or `null` when absent
 * - `message`     — a diagnosis, not a generic failure: names the URL AND the
 *                   two causes worth checking first.
 *
 * @example
 * throw new HtmlResponseError({
 *   endpoint: '/user-preferences/list-views',
 *   method: 'GET',
 *   status: 200,
 *   contentType: 'text/html',
 * });
 */
export class HtmlResponseError extends Error {
  /**
   * @param {Object} opts
   * @param {string} opts.endpoint - Request URL as written by the caller (required).
   * @param {string} opts.method - HTTP method (required, non-empty).
   * @param {number} opts.status - HTTP status that carried the HTML body.
   * @param {string|null} [opts.contentType] - Response content-type header.
   * @param {string} [opts.requestUrl] - Fully composed URL, when known.
   * @param {unknown} [opts.cause] - Chained-error reference (ES2022).
   */
  constructor(opts) {
    if (!opts || typeof opts !== 'object') {
      throw new TypeError('HtmlResponseError: constructor opts are required');
    }
    const { endpoint, method, status, contentType, requestUrl, cause } = opts;
    if (typeof endpoint !== 'string' || endpoint.length === 0) {
      throw new TypeError('HtmlResponseError: opts.endpoint is required (non-empty string)');
    }
    if (typeof method !== 'string' || method.length === 0) {
      throw new TypeError('HtmlResponseError: opts.method is required (non-empty string)');
    }

    const normalizedMethod = method.toLowerCase();
    const message =
      `Expected JSON but received an HTML document from ${normalizedMethod} ${endpoint} ` +
      `(status ${status}). This is almost always a URL that never reached the API: ` +
      `either the request path is missing the API prefix and hit the SPA's history ` +
      `fallback, or the /api route is mis-proxied. Treated as a failure because an ` +
      `HTML body parsed as JSON silently discards the request.`;

    const errOpts = cause === undefined ? undefined : { cause };
    super(message, errOpts);

    this.name = 'HtmlResponseError';
    this.code = 'HTML_RESPONSE';
    this.status = status;
    this.endpoint = endpoint;
    this.requestUrl = typeof requestUrl === 'string' && requestUrl.length > 0 ? requestUrl : endpoint;
    this.method = normalizedMethod;
    this.contentType = typeof contentType === 'string' ? contentType : null;

    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

// Anchor `name` on the prototype so `HtmlResponseError.prototype.name` returns
// the canonical string for contract tests and downstream `err.name` checks.
HtmlResponseError.prototype.name = 'HtmlResponseError';
